import { Stripe } from '@stripe/stripe-js';
import { MBox, MLink, MText } from '../components/Monetize';

/**
 * Shows a toast message for microdeposit verification
 * Returns the verification URL if available
 */
export const showMicrodepositVerificationToast = (
  addToast: (toast: {
    summary: string;
    detail?: string;
    severity: 'info' | 'success' | 'error' | 'warning';
    life?: number;
    customContent?: React.ReactElement;
  }) => void,
  verificationUrl?: string,
): string | undefined => {
  addToast({
    summary: 'Verification Required',
    severity: 'info',
    life: 10000, // Show for 10 seconds
    customContent: (
      <MBox>
        <MText>
          The bank account requires additional verification. Check your email
          for further instructions.
        </MText>
        {verificationUrl && (
          <MLink
            href={verificationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here to verify your bank account now →
          </MLink>
        )}
      </MBox>
    ),
  });

  return verificationUrl;
};

export const processClientSecretId = async (
  stripe: Stripe,
  clientSecretId: string,
) => {
  const setupIntentResp = await stripe.retrieveSetupIntent(clientSecretId);
  const { setupIntent } = setupIntentResp;

  let message = '';
  if (!setupIntent) {
    throw new Error('Sorry, failed to connect.');
  }
  // Some payment methods will [immediately succeed or fail][0] upon
  // confirmation, while others will first enter a `processing` state.

  // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
  switch (setupIntent.status) {
    case 'succeeded': {
      message = 'Success! Your payment method has been saved.';
      break;
    }

    case 'processing': {
      message =
        'Your payment method has been saved successfully and is awaiting verification.';
      break;
    }

    case 'requires_action': {
      message =
        'Your payment method requires additional verification. Please complete the verification process.';
      break;
    }

    case 'requires_payment_method': {
      message =
        'Failed to process payment details. Please try another payment method.';

      throw new Error('There was a problem saving your Payment Method.');
    }

    default: {
      throw new Error('There was a problem saving your Payment Method.');
    }
  }

  return { message, setupIntent };
};
